import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Form,
  Input,
  message,
  Select,
  Spin,
  Tag,
} from 'antd'
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LinkIcon from '@mui/icons-material/Link'
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt'
import SettingsIcon from '@mui/icons-material/Settings'
import TuneIcon from '@mui/icons-material/Tune'
import ImageIcon from '@mui/icons-material/Image'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import TimerIcon from '@mui/icons-material/Timer'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useNavigate, useParams } from 'react-router-dom'

import { ContentWrapper } from '../../../../components/ContentWrapper'
import Helmet from '../../../../components/Helmet'
import HeaderContent from '../../../../components/Layouts/HeaderContent'
import { axiosInstance } from '../../../../hooks/useAxios'
import PageUrls from '../../../../constants/page-urls'
import { BlockEditor } from '../BlockEditor'
import { SalepagePreviewModal } from '../SalepagePreview/SalepagePreviewModal'
import { SeoThemeTab } from './SeoThemeTab'
import type {
  SalepageMetaValues,
  SalepageThemeValues,
} from './SeoThemeTab'
import type { IBlock, BlockType } from '../../types'
import { BLOCK_TYPE_META } from '../../types'

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft — เก็บแบบร่าง ยังไม่เผยแพร่' },
  { value: 'published', label: 'Published — เผยแพร่ให้ลูกค้าเข้าชมได้' },
]

const SLUG_PATTERN = /^[a-zA-Z0-9_-]+$/
const SLUG_DEBOUNCE_MS = 400

const BLOCK_SUMMARY_ICONS: Record<BlockType, React.ReactNode> = {
  IMAGE: <ImageIcon sx={{ fontSize: 14 }} />,
  VIDEO: <VideoLibraryIcon sx={{ fontSize: 14 }} />,
  CONTENT: <TextFieldsIcon sx={{ fontSize: 14 }} />,
  SLIDER: <ViewCarouselIcon sx={{ fontSize: 14 }} />,
  FORM: <ShoppingCartIcon sx={{ fontSize: 14 }} />,
  COUNTDOWN: <TimerIcon sx={{ fontSize: 14 }} />,
  BANK_ACCOUNTS: <AccountBalanceIcon sx={{ fontSize: 14 }} />,
}

interface SalepageDetailResponse {
  id: string
  merchant: string
  slug: string
  title: string
  status: 'draft' | 'published'
  blocks?: IBlock[]
  meta?: SalepageMetaValues
  theme?: SalepageThemeValues
  createdAt: string
  updatedAt: string
}

const SalepageDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingBlocks, setSavingBlocks] = useState(false)
  const [savingSeoTheme, setSavingSeoTheme] = useState(false)
  const [meta, setMeta] = useState<SalepageMetaValues | undefined>()
  const [theme, setTheme] = useState<SalepageThemeValues | undefined>()
  const [blocks, setBlocks] = useState<IBlock[]>([])
  const [activeTab, setActiveTab] = useState(0)
  const slug = Form.useWatch('slug', form)
  const title = Form.useWatch('title', form)
  const status = Form.useWatch('status', form)
  const [slugCheck, setSlugCheck] = useState<
    'idle' | 'checking' | 'available' | 'taken' | 'invalid'
  >('idle')
  const slugCheckRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)

  useEffect(() => {
    if (!id) {
      setNotFound(true)
      setLoading(false)
      return
    }
    let cancelled = false
    const fetchDetail = async () => {
      try {
        const res = await axiosInstance.get<
          SalepageDetailResponse | { data: SalepageDetailResponse }
        >(`/salepage/${id}`)
        if (cancelled) return
        const data = res.data
        const d =
          data && typeof data === 'object' && 'data' in data
            ? (data as { data: SalepageDetailResponse }).data
            : (data as SalepageDetailResponse)
        form.setFieldsValue({
          title: d.title,
          slug: d.slug,
          status: d.status,
        })
        setBlocks(
          (d.blocks ?? []).map((b: IBlock, i: number) => ({
            ...b,
            order: typeof b.order === 'number' ? b.order : i,
          })),
        )
        setMeta((d.meta ?? {}) as SalepageMetaValues)
        setTheme((d.theme ?? {}) as SalepageThemeValues)
      } catch (e: unknown) {
        if (cancelled) return
        const status = (e as { response?: { status?: number } })?.response
          ?.status
        if (status === 404) setNotFound(true)
        else message.error('โหลดข้อมูลไม่สำเร็จ')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchDetail()
    return () => {
      cancelled = true
    }
  }, [id, form])

  useEffect(() => {
    if (!id || !slug || typeof slug !== 'string') {
      setSlugCheck('idle')
      return
    }
    const trimmed = slug.trim()
    if (!trimmed) {
      setSlugCheck('idle')
      return
    }
    if (!SLUG_PATTERN.test(trimmed)) {
      setSlugCheck('invalid')
      return
    }
    if (slugCheckRef.current) clearTimeout(slugCheckRef.current)
    slugCheckRef.current = setTimeout(() => {
      setSlugCheck('checking')
      axiosInstance
        .get<boolean>('/salepage/check-slug', {
          params: { slug: trimmed, excludeId: id },
        })
        .then(({ data }) =>
          setSlugCheck(data === true ? 'available' : 'taken'),
        )
        .catch(() => setSlugCheck('idle'))
      slugCheckRef.current = null
    }, SLUG_DEBOUNCE_MS)
    return () => {
      if (slugCheckRef.current) clearTimeout(slugCheckRef.current)
    }
  }, [id, slug])

  const handleSaveBasic = useCallback(async () => {
    if (!id) return
    if (slugCheck === 'taken' || slugCheck === 'invalid') {
      message.warning('กรุณาแก้ไข Slug ให้ถูกต้องและไม่ซ้ำกับหน้าอื่น')
      return
    }
    try {
      const values = await form.validateFields()
      setSaving(true)
      await axiosInstance.put(`/salepage/${id}`, {
        slug: values.slug?.trim(),
        title: values.title?.trim(),
        status: values.status ?? 'draft',
      })
      message.success('บันทึกข้อมูลหลักสำเร็จ')
    } catch (e: unknown) {
      const err = e as { errorFields?: unknown }
      if (err?.errorFields) return
      const msg = (e as { response?: { data?: { message?: string } } })
        ?.response?.data?.message
      message.error(msg ?? 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }, [id, form, slugCheck])

  const handleSaveBlocks = useCallback(async () => {
    if (!id) return
    try {
      setSavingBlocks(true)
      await axiosInstance.patch(`/salepage/${id}/blocks`, {
        blocks: blocks.map((b) => ({
          type: b.type,
          order: b.order,
          payload: b.payload,
          settings: b.settings,
        })),
      })
      message.success('บันทึก Blocks สำเร็จ')
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })
        ?.response?.data?.message
      message.error(msg ?? 'บันทึก Blocks ไม่สำเร็จ')
    } finally {
      setSavingBlocks(false)
    }
  }, [id, blocks])

  const handleSaveMetaTheme = useCallback(
    async (metaValues: SalepageMetaValues, themeValues: SalepageThemeValues) => {
      if (!id) return
      try {
        setSavingSeoTheme(true)
        await axiosInstance.put(`/salepage/${id}`, {
          meta: metaValues,
          theme: themeValues,
        })
        setMeta(metaValues)
        setTheme(themeValues)
        message.success('บันทึก SEO & Theme สำเร็จ')
      } catch (e: unknown) {
        const msg = (e as { response?: { data?: { message?: string } } })
          ?.response?.data?.message
        message.error(msg ?? 'บันทึก SEO & Theme ไม่สำเร็จ')
      } finally {
        setSavingSeoTheme(false)
      }
    },
    [id],
  )

  const handleSaveTheme = useCallback(
    async (themeValues: SalepageThemeValues) => {
      if (!id) return
      try {
        setSavingSeoTheme(true)
        await axiosInstance.put(`/salepage/${id}`, { theme: themeValues })
        setTheme(themeValues)
        message.success('บันทึก Theme สำเร็จ')
      } catch (e: unknown) {
        const msg = (e as { response?: { data?: { message?: string } } })
          ?.response?.data?.message
        message.error(msg ?? 'บันทึก Theme ไม่สำเร็จ')
      } finally {
        setSavingSeoTheme(false)
      }
    },
    [id],
  )

  const slugToUrl = (s: string) =>
    s ? `${process.env.REACT_APP_SALEPAGE_BASE_URL}/${s}` : ''

  const slugHelp = () => {
    if (slugCheck === 'checking')
      return (
        <Typography component="span" variant="caption" color="text.secondary">
          กำลังตรวจสอบ Slug…
        </Typography>
      )
    if (slugCheck === 'available')
      return <Tag color="success">Slug นี้ใช้ได้</Tag>
    if (slugCheck === 'taken')
      return <Tag color="error">Slug นี้ถูกใช้แล้ว</Tag>
    if (slugCheck === 'invalid' && slug?.trim())
      return (
        <Tag color="warning">ใช้เฉพาะตัวอักษร ตัวเลข ขีดกลาง และขีดล่าง</Tag>
      )
    return null
  }

  const isPublished = status === 'published'
  const isBusy = saving || savingBlocks || savingSeoTheme

  const Menus = [
    {
      breadcrumbName: 'Salepage',
      title: 'Salepage',
      path: PageUrls.Salepage.Index,
    },
    {
      breadcrumbName: 'แก้ไข',
      title: 'แก้ไข Salepage',
      path: `${PageUrls.Salepage.Index}/${id}`,
    },
  ]

  if (loading) {
    return (
      <ContentWrapper>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Spin size="large" />
        </Box>
      </ContentWrapper>
    )
  }

  if (notFound || !id) {
    return (
      <ContentWrapper>
        <Alert
          type="warning"
          message="ไม่พบหน้า Salepage"
          description="อาจถูกลบหรือไม่มีสิทธิ์เข้าถึง"
        />
        <Button
          variant="contained"
          onClick={() => navigate(PageUrls.Salepage.Index)}
        >
          กลับไปรายการ
        </Button>
      </ContentWrapper>
    )
  }

  return (
    <ContentWrapper>
      <Helmet title="แก้ไข Salepage" />
      <HeaderContent
        title="แก้ไข Salepage"
        routes={Menus}
        descriptions="แก้ไขข้อมูลหลักและ Content Blocks"
      />

      <Box
        sx={{
          display: 'flex',
          gap: 3,
          flexWrap: { xs: 'wrap', lg: 'nowrap' },
          alignItems: 'flex-start',
        }}
      >
        {/* Main editor column */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              mb: 2,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
            }}
          >
            <Tab
              icon={<ViewQuiltIcon />}
              iconPosition="start"
              label="Content Blocks"
            />
            <Tab
              icon={<SettingsIcon />}
              iconPosition="start"
              label="Page Settings"
            />
            <Tab
              icon={<TuneIcon />}
              iconPosition="start"
              label="SEO & Theme"
            />
          </Tabs>

          {/* Tab: Content Blocks */}
          <Box sx={{ display: activeTab === 0 ? 'block' : 'none' }}>
            <BlockEditor
              value={blocks}
              onChange={setBlocks}
              disabled={savingBlocks}
            />
            <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                disabled={savingBlocks}
                startIcon={<SaveIcon />}
                onClick={handleSaveBlocks}
              >
                {savingBlocks ? 'กำลังบันทึก...' : 'บันทึก Blocks'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<VisibilityIcon />}
                onClick={() => setPreviewOpen(true)}
                disabled={blocks.length === 0}
              >
                Preview
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(PageUrls.Salepage.Index)}
              >
                กลับไปรายการ
              </Button>
            </Box>
          </Box>

          {/* Tab: SEO & Theme */}
          <Box sx={{ display: activeTab === 2 ? 'block' : 'none' }}>
            <SeoThemeTab
              meta={meta}
              theme={theme}
              onSave={handleSaveMetaTheme}
              saving={savingSeoTheme}
              onPreview={() => setPreviewOpen(true)}
            />
          </Box>

          {/* Tab: Page Settings */}
          <Box sx={{ display: activeTab === 1 ? 'block' : 'none' }}>
            <Card
              sx={{
                p: 3,
                borderRadius: 2,
                boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
              }}
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSaveBasic}
                initialValues={{ status: 'draft' }}
              >
                <Form.Item
                  name="title"
                  label="ชื่อหัวข้อ Salepage"
                  rules={[
                    { required: true, message: 'กรุณากรอกชื่อหน้า' },
                  ]}
                >
                  <Input
                    placeholder="ใส่ชื่อหัวข้อ..."
                    size="large"
                    allowClear
                  />
                </Form.Item>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 2 }}
                >
                  แสดงเป็น &lt;title&gt; ของหน้า
                </Typography>

                <Form.Item
                  name="slug"
                  label="Slug / Endpoint URL"
                  validateStatus={
                    slugCheck === 'taken' || slugCheck === 'invalid'
                      ? 'error'
                      : slugCheck === 'available'
                        ? 'success'
                        : undefined
                  }
                  help={slugHelp()}
                  rules={[
                    { required: true, message: 'กรุณากรอก Slug' },
                    {
                      pattern: SLUG_PATTERN,
                      message:
                        'ใช้เฉพาะตัวอักษร ตัวเลข ขีดกลาง (-) และขีดล่าง (_)',
                    },
                  ]}
                >
                  <Input
                    addonBefore="/s/"
                    placeholder="my-landing-page"
                    size="large"
                    allowClear
                  />
                </Form.Item>
                {slug?.trim() && (
                  <Box sx={{ mb: 2 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        bgcolor: 'rgba(46, 125, 50, 0.08)',
                        borderColor: 'rgba(46, 125, 50, 0.3)',
                      }}
                    >
                      <LinkIcon
                        sx={{ color: 'success.main', fontSize: 20 }}
                      />
                      <Box sx={{ flex: 1, mt: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">
                          URL สำหรับแชร์
                        </Typography>
                        <Typography
                          variant="body2"
                          component="code"
                          sx={{
                            display: 'block',
                            fontFamily: 'monospace',
                            color: 'success.main',
                            wordBreak: 'break-all',
                          }}
                        >
                          {slugToUrl(slug.trim())}
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Form.Item
                  name="status"
                  label="สถานะการเผยแพร่"
                  tooltip="Draft = เก็บแบบร่าง, Published = เผยแพร่ให้เข้าชมได้"
                >
                  <Select size="large" options={STATUS_OPTIONS} />
                </Form.Item>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    disabled={
                      slugCheck === 'taken' ||
                      slugCheck === 'invalid' ||
                      slugCheck === 'checking' ||
                      saving
                    }
                    startIcon={<SaveIcon />}
                    onClick={() => form.submit()}
                  >
                    {saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูลหลัก'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(PageUrls.Salepage.Index)}
                  >
                    กลับไปรายการ
                  </Button>
                </Box>
              </Form>
            </Card>
          </Box>
        </Box>

        {/* Sidebar */}
        <Paper
          variant="outlined"
          sx={{
            width: { xs: '100%', lg: 272 },
            flexShrink: 0,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ letterSpacing: 1.2, textTransform: 'uppercase' }}
            >
              Page Info
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                ชื่อหน้า
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {title?.trim() || '—'}
              </Typography>
            </Box>
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Slug
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ fontFamily: 'monospace', color: 'success.main' }}
              >
                /{slug?.trim() || '—'}
              </Typography>
            </Box>
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                จำนวน Blocks
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {blocks.length} blocks
              </Typography>
            </Box>
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                สถานะ
              </Typography>
              <Chip
                size="small"
                label={isPublished ? 'Published' : 'Draft'}
                color={isPublished ? 'success' : 'warning'}
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Box>

          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ letterSpacing: 1.2, textTransform: 'uppercase' }}
            >
              Blocks Summary
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              {(Object.keys(BLOCK_TYPE_META) as BlockType[]).map((type) => {
                const meta = BLOCK_TYPE_META[type]
                const count = blocks.filter((b) => b.type === type).length
                return (
                  <Box
                    key={type}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      py: 0.75,
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 1,
                        bgcolor: `${meta.color}22`,
                        color: meta.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {BLOCK_SUMMARY_ICONS[type]}
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ flex: 1 }}
                    >
                      {meta.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color={count === 0 ? 'text.disabled' : 'text.primary'}
                    >
                      {count}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </Box>

          <Box sx={{ p: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              size="large"
              disabled={isBusy}
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(PageUrls.Salepage.Index)}
            >
              กลับไปรายการ
            </Button>
          </Box>
        </Paper>
      </Box>

      <SalepagePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        blocks={blocks}
        title={title?.trim()}
        theme={theme}
        onSaveTheme={handleSaveTheme}
      />
    </ContentWrapper>
  )
}

export default SalepageDetailPage
